import type { ElementType, ReactNode } from 'react'

import { useMemo } from 'react'
import { cn } from '@/sanskrit/lib/utils'
import { toIAST, tokenizeSentence } from '@/sanskrit/lib/sanskrit/script'
import { useAppState } from '@/sanskrit/components/providers/app-state'
import { useUi } from '@/sanskrit/components/providers/ui-state'

/** Whether transliteration should be shown right now (auto: fades out after the first six lessons). */
export const useShowIast = (): boolean => {
    const { settings, progress, hydrated } = useAppState()
    if (settings.iast === 'always') return true
    if (settings.iast === 'never') return false
    if (!hydrated) return true
    return Object.values(progress.lessons).filter(l => l.passed).length < 6
}

interface SktProps {
    text: string
    hints?: Record<string, string>
    className?: string
    wordClassName?: string
    as?: ElementType
    clickable?: boolean
    iast?: boolean | 'auto'
    iastClassName?: string
    activeWord?: number
    context?: string
}

/**
 * Sanskrit text in which every word is a button that opens the Word Detail Panel.
 * Separators (spaces, daṇḍas, punctuation) are kept exactly as written.
 */
export default function Skt({
    text,
    hints,
    className,
    wordClassName,
    as: Tag = 'span',
    clickable = true,
    iast = false,
    iastClassName,
    activeWord,
    context
}: SktProps) {
    const { openWord } = useUi()
    const autoIast = useShowIast()
    const tokens = useMemo(() => tokenizeSentence(text), [text])
    const showIast = iast === 'auto' ? autoIast : iast

    let wordIndex = -1

    const content: ReactNode[] = tokens.map((token, i) => {
        if (!token.isWord) return <span key={i}>{token.text}</span>

        wordIndex++
        const index = wordIndex

        if (!clickable) {
            return (
                <span key={i} data-active={activeWord === index ? 'true' : undefined} className={cn('skt-word', wordClassName)}>
                    {token.text}
                </span>
            )
        }

        return (
            <button
                key={i}
                type='button'
                lang='sa'
                data-active={activeWord === index ? 'true' : undefined}
                onClick={event => {
                    event.stopPropagation()
                    openWord({ word: token.text, hint: hints?.[token.text], context: context ?? text })
                }}
                className={cn('skt-word -mx-0.5 inline cursor-pointer px-0.5 text-left', wordClassName)}
                aria-label={`${token.text}: show meaning and grammar`}
            >
                {token.text}
            </button>
        )
    })

    if (!showIast) {
        return (
            <Tag lang='sa' className={cn('deva', className)}>
                {content}
            </Tag>
        )
    }

    return (
        <Tag className={cn('inline-flex flex-col', className)}>
            <span lang='sa' className='deva'>
                {content}
            </span>
            <span lang='sa-Latn' className={cn('iast mt-0.5 text-[0.62em] font-normal text-muted-foreground', iastClassName)}>
                {toIAST(text)}
            </span>
        </Tag>
    )
}

export function Iast({ text, className }: { text: string; className?: string }) {
    return (
        <span lang='sa-Latn' className={cn('iast text-muted-foreground', className)}>
            {toIAST(text)}
        </span>
    )
}
